// Earnings Calendar types
export interface EarningsCalendarEntry {
  id?: string
  symbol: string
  name?: string
  report_date: string
  fiscal_date_ending?: string
  estimate?: number
  currency: string
  days_until: number
  created_at?: string
}

export interface EarningsCalendarResponse {
  entries: EarningsCalendarEntry[]
  total_count: number
  holdings_with_earnings: number
  next_earnings_date?: string
}

// Earnings History types
export interface EarningsHistoryEntry {
  id?: string
  symbol: string
  fiscal_date_ending: string
  reported_date: string
  reported_eps?: number
  estimated_eps?: number
  surprise?: number
  surprise_pct?: number
  price_close_before?: number
  price_close_after?: number
  price_change_pct?: number
  created_at?: string
}

export interface EarningsHistoryResponse {
  symbol: string
  history: EarningsHistoryEntry[]
  total_quarters: number
}

// Earnings Summary types
export interface EarningsSummary {
  symbol: string
  next_earnings_date?: string
  days_until_earnings?: number
  beat_count: number
  miss_count: number
  meet_count: number
  total_quarters: number
  beat_rate_pct: number
  avg_surprise_pct: number
  streak: number
  avg_price_reaction_pct?: number
}

export interface EarningsSummaryResponse {
  summary: EarningsSummary
}

// Refresh operations
export interface EarningsRefreshResponse {
  success: boolean
  message: string
  entries_updated: number
  symbols_processed: string[]
}

// Helper functions
export function getEarningsUrgencyColor(daysUntil: number): string {
  if (daysUntil <= 7) return 'bg-red-100 text-red-800 border-red-200'
  if (daysUntil <= 14) return 'bg-yellow-100 text-yellow-800 border-yellow-200'
  return 'bg-gray-100 text-gray-800 border-gray-200'
}

export function getEarningsUrgencyLabel(daysUntil: number): string {
  if (daysUntil === 0) return 'Today'
  if (daysUntil === 1) return 'Tomorrow'
  if (daysUntil <= 7) return `${daysUntil}d`
  if (daysUntil <= 14) return `${daysUntil}d`
  return `${daysUntil}d`
}

export function getSurpriseColor(surprisePct: number | undefined): string {
  if (surprisePct === undefined) return 'text-gray-500'
  if (surprisePct > 0) return 'text-green-600'
  if (surprisePct < 0) return 'text-red-600'
  return 'text-gray-600'
}

export function formatSurprise(surprisePct: number | undefined): string {
  if (surprisePct === undefined) return '-'
  const sign = surprisePct >= 0 ? '+' : ''
  return `${sign}${surprisePct.toFixed(1)}%`
}

export function getStreakLabel(streak: number): string {
  if (streak === 0) return 'No streak'
  if (streak > 0) return `${streak} beat${streak > 1 ? 's' : ''}`
  return `${Math.abs(streak)} miss${Math.abs(streak) > 1 ? 'es' : ''}`
}

export function getStreakColor(streak: number): string {
  if (streak === 0) return 'text-gray-500'
  if (streak > 0) return 'text-green-600'
  return 'text-red-600'
}
